import { useMemo, useState, Fragment } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import toast from "react-hot-toast";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";

export default function PermissionTable() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    headerGrad: isDark
      ? "linear-gradient(90deg, #2E7D32 0%, #1B5E20 50%, #2E7D32 100%)"
      : "linear-gradient(90deg, #E8F5E9 0%, #DFF4EA 60%, #E8F5E9 100%)",
    groupGrad: isDark
      ? "linear-gradient(90deg, #1B5E20 0%, #2E7D32 60%, #1B5E20 100%)"
      : "linear-gradient(90deg, #E8F5E9 0%, #DFF4EA 60%, #E8F5E9 100%)",
    zebraOverlay: isDark
      ? "linear-gradient(0deg, rgba(255,255,255,0.04), rgba(255,255,255,0.04))"
      : "linear-gradient(0deg, rgba(0,0,0,0.03), rgba(0,0,0,0.03))",
    footerGrad: isDark
      ? "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(38,50,56,0.9) 60%, #263238 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(240,244,248,0.9) 60%, #F0F4F8 100%)",
    border: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    primary: "#43A047",
    paperBg: isDark ? "#1E1E1E" : "#fff",
    textColor: isDark ? "#E0E0E0" : "#212121",
  };

  const roles = ["ผู้จัดการ", "แคชเชียร์", "เครื่องชั่ง"];

  const permissionGroups = [
    {
      group: "หมวดการซื้อ",
      items: [
        {
          key: "purchase_edit_total_weight",
          label: "แก้ไขน้ำหนักรวมจากเครื่องคิดเลขหรือคีย์บอร์ด",
        },
        {
          key: "purchase_edit_deduct_weight",
          label: "แก้ไขน้ำหนักหักจากเครื่องคิดเลขหรือคีย์บอร์ด",
        },
        {
          key: "purchase_edit_impurity",
          label: "แก้ไขสิ่งเจือปนจากเครื่องคิดเลขหรือคีย์บอร์ด",
        },
        {
          key: "purchase_edit_price",
          label: "แก้ไขราคาสินค้าในตั๋วจากเครื่องคิดเลขหรือคีย์บอร์ด",
        },
        { key: "purchase_view_user", label: "เห็นผู้ใช้งานเมื่อเปิดดูตั๋ว" },
        { key: "purchase_pay_cash", label: "สั่งจ่ายเงิน" },
        { key: "purchase_pay_atm", label: "สั่งจ่ายด้วย ATM" },
        { key: "purchase_pay_promptpay", label: "สั่งจ่ายด้วยพร้อมเพย์" },
        {
          key: "purchase_cancel_payment",
          label: "ยกเลิกการจ่ายเงิน (ตั๋วจ่ายแล้ว)",
        },
        { key: "purchase_cancel_ticket", label: "ยกเลิกตั๋ว" },
        { key: "purchase_history", label: "ประวัติตั๋ว" },
      ],
    },
    {
      group: "จัดการสินค้า",
      items: [{ key: "product_manage", label: "จัดการสินค้า" }],
    },
    {
      group: "รายงาน",
      items: [
        { key: "report_purchase_items", label: "รายงานการซื้อสินค้า" },
        { key: "report_sale_items", label: "รายงานการขายสินค้า" },
        {
          key: "report_show_cost",
          label: "แสดงต้นทุนในรายงานขาย (ต้นทุนต่อหน่วย และต้นทุนรวม)",
        },
        {
          key: "report_show_profit",
          label: "แสดงกำไรในรายงานขาย (กำไรต่อหน่วย และกำไรรวม)",
        },
      ],
    },
    {
      group: "จัดการลูกค้า",
      items: [
        {
          key: "customer_manage",
          label: "จัดการลูกค้า (ไม่รวมการสร้างลูกค้าใหม่เมื่อเปิดตั๋ว)",
        },
      ],
    },
    {
      group: "จัดการกลุ่มลูกค้า",
      items: [{ key: "customer_group_manage", label: "จัดการกลุ่มลูกค้า" }],
    },
    {
      group: "หมวดการขาย",
      items: [{ key: "sale_manage", label: "หมวดการขาย" }],
    },
    {
      group: "สต๊อกสินค้า",
      items: [
        { key: "inventory_access", label: "การเข้าถึง" },
        {
          key: "inventory_view_changes",
          label: "เห็นการเปลี่ยนแปลงของต้นทุนและคลัง",
        },
      ],
    },
    {
      group: "จัดการใบเสนอราคา",
      items: [{ key: "quotation_manage", label: "จัดการใบเสนอราคา (PO)" }],
    },
    {
      group: "จัดการตู้ ATM",
      items: [{ key: "atm_manage", label: "จัดการตู้ ATM" }],
    },
  ];

  const [permissions, setPermissions] = useState(() => {
    const init = {};
    roles.forEach((r) => (init[r] = {}));
    return init;
  });

  const toggle = (role, key) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: { ...prev[role], [key]: !prev[role][key] },
    }));
  };

  const handleSave = async () => {
    try {
      const res = await fetch("/api/permissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(permissions),
      });
      if (!res.ok) throw new Error("บันทึกไม่สำเร็จ");
      toast.success("บันทึกสิทธิ์สำเร็จ 🎉");
    } catch (e) {
      toast.error(e?.message ?? "เกิดข้อผิดพลาด");
    }
  };

  return (
    <>
      <AppBreadcrumbs />
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: `1px solid ${ui.border}`,
          bgcolor: ui.paperBg,
          color: ui.textColor,
          mt: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            pb: 1.5,
            color: "white",
            borderBottom: `1px solid ${ui.border}`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            background:
              "linear-gradient(90deg, #2E7D32 0%, #43A047 50%, #66BB6A 100%)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            สิทธิ์ในการเข้าถึง
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.9, color: "rgba(255,255,255,0.85)" }}
          >
            ตั้งค่าสิทธิ์แยกตามหมวด และกำหนดได้รายบทบาทผู้ใช้งาน
          </Typography>
        </Box>

        <Divider />

        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 340px)",
            "& .MuiTableCell-root": { fontSize: 14, color: ui.textColor },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    background: ui.headerGrad,
                    borderRight: `1px solid ${ui.border}`,
                    minWidth: 420,
                    color: isDark ? "#E0E0E0" : "#212121",
                    height: 50,
                  }}
                >
                  รายการสิทธิ์
                </TableCell>
                {roles.map((r) => (
                  <TableCell
                    key={r}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      background: ui.headerGrad,
                      minWidth: 140,
                      color: isDark ? "#E0E0E0" : "#212121",
                    }}
                  >
                    {r}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {permissionGroups.map((group) => (
                <Fragment key={group.group}>
                  <TableRow>
                    <TableCell
                      colSpan={1 + roles.length}
                      sx={{
                        background: ui.groupGrad,
                        color: isDark ? "#E0E0E0" : "text.primary",
                        fontWeight: 700,
                        borderTop: `1px solid ${ui.border}`,
                        height: 50,
                      }}
                    >
                      {group.group}
                    </TableCell>
                  </TableRow>

                  {group.items.map((item) => (
                    <TableRow
                      key={item.key}
                      hover
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundImage: ui.zebraOverlay,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          borderRight: `1px solid ${ui.border}`,
                        }}
                      >
                        {item.label}
                      </TableCell>

                      {roles.map((r) => (
                        <TableCell align="center" key={`${r}-${item.key}`}>
                          <Checkbox
                            checked={!!permissions[r][item.key]}
                            onChange={() => toggle(r, item.key)}
                            sx={{
                              "&.Mui-checked": { color: ui.primary },
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            background: ui.footerGrad,
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            borderTop: `1px solid ${ui.border}`,
            backdropFilter: "saturate(120%) blur(6px)",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              background:
                "linear-gradient(90deg, #43A047 0%, #4CAF50 50%, #66BB6A 100%)",
              boxShadow:
                "0 6px 16px rgba(76,175,80,0.28), 0 2px 6px rgba(0,0,0,0.06)",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #388E3C 0%, #43A047 50%, #4CAF50 100%)",
              },
            }}
          >
            บันทึกสิทธิ์
          </Button>
        </Box>
      </Paper>
    </>
  );
}
